export class ViewMigrationError extends Error {
    code;
    path;
    constructor(code, path, message) {
        super(message);
        this.code = code;
        this.path = path;
        this.name = 'ViewMigrationError';
    }
}
/** Parties de 2.x sans composant au registre Core : une vue qui les porte n'est pas convertie. */
const UNSUPPORTED = ['map', 'timeline', 'wizard', 'tree'];
/** Clés de `list` portées par le conteneur `Collection`, communes à toutes les représentations. */
const COLLECTION_KEYS = ['search', 'quick_filters', 'period', 'filters', 'export', 'totals', 'bulk_commands', 'create_action', 'row_action'];
/** Une carte a six champs au plus (59 §5.3 ; `card_template.fields`). */
const CARD_FIELDS_MAX = 6;
/** Vrai pour un corps 2.x : il nomme sa représentation d'entrée par `view_kind`, un corps 3.0 par `entry`. */
export function isViewBodyV2(body) {
    return typeof body === 'object' && body !== null && typeof body.view_kind === 'string';
}
export function migrateViewBody(v2) {
    for (const part of UNSUPPORTED) {
        if (v2[part] !== undefined || v2.view_kind === part) {
            throw new ViewMigrationError('VIEW_PART_UNSUPPORTED', `/${part}`, `« ${part} » n'a pas encore de composant au registre Core (ADR-099) : la vue reste en 2.x.`);
        }
    }
    if (v2.view_kind === 'decision') {
        throw new ViewMigrationError('VIEW_PART_UNSUPPORTED', '/view_kind', 'Une vue « decision » attend le composant DecisionCard au registre Core (ADR-099) : la vue reste en 2.x.');
    }
    const representations = [];
    const collectionIds = v2.representations ?? ['list', 'kanban', 'agenda'].filter((id) => v2[id] !== undefined);
    for (const id of collectionIds) {
        if (id === 'list' && v2.list !== undefined)
            representations.push(...listRepresentations(v2.list));
        if (id === 'kanban' && v2.kanban !== undefined)
            representations.push({ id: 'kanban', component: 'Kanban', inputs: kanbanInputs(v2.kanban) });
        if (id === 'agenda' && v2.agenda !== undefined)
            representations.push({ id: 'agenda', component: 'Agenda', inputs: { ...v2.agenda } });
    }
    if (v2.detail !== undefined)
        representations.push({ id: 'detail', component: 'Detail', inputs: { ...v2.detail } });
    if (v2.form !== undefined)
        representations.push({ id: 'form', component: 'Form', inputs: { ...v2.form } });
    if (v2.dashboard !== undefined)
        representations.push({ id: 'dashboard', component: 'Dashboard', inputs: { ...v2.dashboard } });
    const entry = v2.view_kind === 'list' ? (v2.list?.layouts?.[0] ?? 'table') : v2.view_kind;
    if (!representations.some((representation) => representation.id === entry)) {
        throw new ViewMigrationError('VIEW_ENTRY_MISSING', '/view_kind', `La représentation d'entrée « ${v2.view_kind} » n'a pas de sous-corps.`);
    }
    const body = {
        entry,
        presentation: v2.presentation,
        title: v2.title,
        data_source: v2.data_source,
        representations: representations,
    };
    if (v2.description !== undefined)
        body.description = v2.description;
    if (v2.target_object_type !== undefined)
        body.target_object_type = v2.target_object_type;
    if (v2.required_permission !== undefined)
        body.required_permission = v2.required_permission;
    if (v2.terminology_ref !== undefined)
        body.terminology_ref = v2.terminology_ref;
    if (v2.surface !== undefined)
        body.surface = v2.surface;
    if (v2.actions !== undefined)
        body.actions = v2.actions;
    if (representations.some((representation) => isCollectionRepresentation(representation.component))) {
        body.collection = { inputs: collectionInputs(v2.list) };
    }
    if (v2.related_views !== undefined)
        body.related_views = v2.related_views;
    return body;
}
/** Représentations servies par le conteneur `Collection` (59 §5.1). */
export function isCollectionRepresentation(component) {
    return component === 'Table' || component === 'CardList' || component === 'Kanban' || component === 'Agenda' || component === 'WeekGrid';
}
function collectionInputs(list) {
    const inputs = {};
    if (list !== undefined) {
        for (const key of COLLECTION_KEYS) {
            if (list[key] !== undefined)
                inputs[key] = list[key];
        }
    }
    return inputs;
}
function listRepresentations(list) {
    const table = { columns: list.columns };
    if (list.group_by !== undefined)
        table.group_by = list.group_by;
    return (list.layouts ?? ['table']).map((layout) => {
        if (layout === 'cards')
            return { id: 'cards', component: 'CardList', inputs: { card: list.card === undefined ? cardFromFields(list.columns.map((column) => column.field)) : cardZones(list.card) } };
        if (layout === 'condensed')
            return { id: 'condensed', component: 'Table', label: { fr: 'Condensé', en: 'Condensed' }, inputs: { ...table, density: 'compact' } };
        return { id: 'table', component: 'Table', inputs: { ...table } };
    });
}
/** Carte tirée d'une liste de champs : le premier en titre, les suivants en champs (six au plus). */
function cardFromFields(fields) {
    const [title, ...rest] = fields;
    return cardZones({ title_field: title ?? 'id', ...(rest.length === 0 ? {} : { fields: rest }) });
}
/**
 * Carte 2.x (titre, sous-titre, image, champs) en zones JSON (ADR-100) : une identité, puis les
 * champs en lignes « libellé : valeur » (six au plus, comme en 2.x).
 */
function cardZones(card) {
    const identity = { type: 'identity', title_field: card.title_field };
    if (card.subtitle_field !== undefined)
        identity.subtitle_field = card.subtitle_field;
    if (card.image_field !== undefined)
        identity.image_field = card.image_field;
    const fields = (card.fields ?? []).slice(0, CARD_FIELDS_MAX);
    // Le contrat borne zones et fields (minItems, maxItems) : les types générés sont des unions de n-uplets.
    const zones = [identity, ...(fields.length === 0 ? [] : [{ type: 'keyvalue', fields }])];
    return { zones: zones };
}
function kanbanInputs(kanban) {
    const columns = kanban.columns.map((column) => {
        const converted = { value: column.value, label: column.label };
        if (column.transition_command !== undefined) {
            converted.events = {
                drop: { actions: [{ action: 'api', service: `command:${column.transition_command}`, params: { target: '$event.target', expected_version: '$event.expected_version' } }] },
            };
        }
        return converted;
    });
    const [first, ...rest] = columns;
    if (first === undefined)
        throw new ViewMigrationError('VIEW_KANBAN_EMPTY', '/kanban/columns', 'Un Kanban déclare au moins une colonne.');
    const inputs = { column_field: kanban.column_field, columns: [first, ...rest] };
    if (kanban.card_fields !== undefined && kanban.card_fields.length > 0)
        inputs.card = cardFromFields(kanban.card_fields);
    return inputs;
}
//# sourceMappingURL=migrate-view-body.js.map